/**
 * Identidad de marca — el ÚNICO lugar donde vive el nombre.
 *
 * Existe por una razón concreta: la marca de cara al cliente residencial todavía
 * no está decidida. La recomendación de estrategia es dejar que la escriban los
 * primeros diez clientes, preguntándoles cómo describirían el servicio a un vecino.
 *
 * Mientras tanto, el sistema no debe quedar amarrado a un nombre provisional. Cuando
 * se decida, **se cambia este archivo y nada más**: ni un componente, ni una ruta, ni
 * una colección de Firestore mencionan la marca.
 *
 * Regla que lo sostiene: los nombres internos son FUNCIONALES —cotizador, clientes,
 * cotizaciones—, nunca de marca. Los nombres de marca solo aparecen en lo que ve el
 * cliente, y todo eso se arma desde aquí.
 */

export const MARCA = {
  /** Nombre comercial que aparece en propuestas, órdenes y encabezados */
  nombre: 'Soham Home',

  /**
   * Nombre de la PLATAFORMA interna (decidido por Juan, 2026-08-17). Distinto del
   * nombre comercial: la Central es la herramienta con la que opera el equipo
   * —cotiza, consulta manuales, administra— y crecerá más allá del cotizador.
   */
  plataforma: 'Soham Central',

  /**
   * Descriptor. Va SIEMPRE junto al nombre, y hace el trabajo que el nombre no hace:
   * decir a qué se dedica. «Soham» no tiene anclaje fonético en español, así que el
   * nombre sirve para identificar y el descriptor para explicar.
   */
  descriptor: 'Instalaciones eléctricas y automatización',

  /** Razón social para documentos con valor legal. Vacío mientras no exista */
  razonSocial: '',

  /** Respaldo. La arquitectura recomendada es marca respaldada: «X, de Soham IoT» */
  respaldo: 'Soham IoT',
  mostrarRespaldo: false,

  /** La frase madre. No es un eslogan: describe el método y se puede verificar en 45 minutos */
  fraseMadre: 'Se mide antes de proponer.',

  /** Credenciales que pesan más que el logotipo para el comprador residencial */
  credenciales: 'Ingeniero titulado · Cédula profesional · Dictamen firmado',

  contacto: {
    telefono: '',
    correo: '',
    sitio: 'home.sohamiot.com'
  },

  /**
   * Domicilio del proveedor. La LFPC (art. 52, leída en la reforma DOF 12-12-2025)
   * exige que toda venta a domicilio conste por escrito con nombre y DIRECCIÓN del
   * proveedor, y que el consumidor reciba copia. Vacío hasta que Juan lo fije; la
   * pantalla de cierre lo avisa al vendedor y la propuesta no lo imprime en blanco.
   */
  domicilio: '',

  /**
   * Derecho de revocación. Texto del art. 56 de la LFPC: el contrato se perfecciona a
   * los cinco días hábiles desde la entrega del bien o la firma (lo último), con
   * revocación sin responsabilidad en ese lapso; NO aplica si el servicio se presta a
   * diez días hábiles o menos de la orden. Se dice completo: es señal de confianza.
   */
  revocacion:
    'Tiene cinco días hábiles desde la firma para cambiar de opinión sin costo (Ley Federal de ' +
    'Protección al Consumidor, art. 56). Si la obra se programa a diez días hábiles o menos de ' +
    'la firma, ese plazo no aplica, y se lo decimos antes de firmar.',

  /**
   * Garantía. La LFPC solo permite la palabra «garantía» con su alcance y cómo hacerla
   * efectiva (art. 40); mínimo 90 días (art. 77). Plazo PROVISIONAL hasta que Juan fije
   * la política; el mecanismo es el canal real del negocio.
   */
  garantia:
    'Garantía de 90 días en mano de obra e instalación a partir de la entrega, y la del ' +
    'fabricante en cada equipo. Se hace efectiva por WhatsApp o correo al proveedor; la visita ' +
    'de garantía queda registrada en la bitácora de la casa.',

  /** Condiciones comerciales que salen impresas en la propuesta */
  condiciones:
    'Las etapas son acumulativas y pueden contratarse por separado. Los importes incluyen ' +
    'material, instalación y transporte, con IVA. Vigencia de 15 días naturales. Condiciones: ' +
    '30% de anticipo, 40% al iniciar la obra y el saldo contra entrega. Esta propuesta se emite ' +
    'tras la revisión física de la instalación.',

  /** Alcance declarado, derivado del dictamen legal. Sale en la orden de servicio */
  alcance: 'vivienda unifamiliar existente, baja tensión, sustitución y mantenimiento'
}

/** Título de la aplicación y de la pestaña del navegador */
export const tituloApp = () => MARCA.plataforma

/** Firma de encabezado para documentos: nombre y, si aplica, su respaldo */
export const encabezadoMarca = () =>
  MARCA.mostrarRespaldo && MARCA.respaldo
    ? `${MARCA.nombre}, de ${MARCA.respaldo}`
    : MARCA.nombre
